import React from 'react';
import { Checkbox, Popover, Button, Input, Typography } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';

// 1 列あたりの最大項目数（これを超えると列を増やす。ただし MAX_COLUMNS まで）
const ITEMS_PER_COLUMN = 10;
// 列数の上限。旧実装は「項目数 ÷ 10」を列数にして antd Col の span を 24 ÷ 列数 で
// 求めていたため、項目が 241 件以上（列数 25 以上）になると span が 0（display:none）となり
// 全項目が非表示になっていた（例: 等級 274 件の企業）。列数を固定上限にして解消する
const MAX_COLUMNS = 3;

/**
 * 複数選択のポップオーバーメニュー（役職・等級・出向状態の絞り込みで使用）
 *
 * props:
 *  - options : string[]   選択肢
 *  - value   : string[]   初期選択（省略可）
 *  - onChange: (selected: string[]) => void
 */
class CheckboxMenu extends React.Component {
  state = {
    selectedItems: [],
    keyword: '', // 検索キーワード（表示の絞り込みのみ。選択状態には影響しない）
  };

  componentDidMount = () => {
    if (this.props.value && this.props.value.length) {
      this.setState({ selectedItems: [...this.props.value] });
    }
  };

  // 検索で絞り込んだ表示用の選択肢
  getVisibleOptions = () => {
    const options = Array.isArray(this.props.options) ? this.props.options : [];
    const kw = this.state.keyword.trim();
    if (!kw) return options;
    return options.filter((label) => String(label).includes(kw));
  };

  // Checkbox.Group は「現在マウントされている選択肢」だけを onChange で返すため、
  // 検索で非表示になっている項目の選択を失わないよう、非表示分と表示分をマージする
  onChange = (visibleSelection) => {
    const options = Array.isArray(this.props.options) ? this.props.options : [];
    const visible = new Set(this.getVisibleOptions());
    const hiddenSelected = this.state.selectedItems.filter(
      (v) => !visible.has(v)
    );
    const nextSet = new Set([...hiddenSelected, ...visibleSelection]);
    // 元の選択肢の順序を維持
    const next = options.filter((label) => nextSet.has(label));
    this.setState({ selectedItems: next });
    return this.props.onChange(next);
  };

  onChangeKeyword = (e) => {
    this.setState({ keyword: e.target.value });
  };

  // 項目を列ごとに均等分配する（列数は上限 MAX_COLUMNS）
  splitIntoColumns = (items) => {
    if (items.length === 0) return [];
    const columnCount = Math.min(
      MAX_COLUMNS,
      Math.ceil(items.length / ITEMS_PER_COLUMN)
    );
    const perColumn = Math.ceil(items.length / columnCount);
    const columns = [];
    for (let i = 0; i < items.length; i += perColumn) {
      columns.push(items.slice(i, i + perColumn));
    }
    return columns;
  };

  renderContent = () => {
    const { selectedItems, keyword } = this.state;
    const visibleOptions = this.getVisibleOptions();
    const columns = this.splitIntoColumns(visibleOptions);
    const total = Array.isArray(this.props.options)
      ? this.props.options.length
      : 0;

    return (
      <div style={{ maxWidth: '90vw' }}>
        {/* ヘッダー: 検索 + 件数表示 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="絞り込み検索"
            value={keyword}
            onChange={this.onChangeKeyword}
            style={{ width: 220 }}
          />
          <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
            選択中 {selectedItems.length} 件 / 全 {total} 件
          </Typography.Text>
        </div>

        {/* 本体: 列分割 + 縦スクロール */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {columns.length === 0 ? (
            <Typography.Text type="secondary">
              該当する項目がありません
            </Typography.Text>
          ) : (
            <Checkbox.Group onChange={this.onChange} value={selectedItems}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-6)',
                }}
              >
                {columns.map((column, i) => (
                  <div
                    key={'checkbox-column-' + i}
                    style={{ minWidth: 220, flex: '1 1 220px' }}
                  >
                    {column.map((label) => (
                      <Checkbox
                        key={label}
                        value={label}
                        style={{ display: 'flex', margin: 0 }}
                      >
                        {label}
                      </Checkbox>
                    ))}
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <Popover
        content={this.renderContent()}
        trigger="click"
        placement="bottomLeft"
      >
        <Button>
          選択 <FilterOutlined />
        </Button>
      </Popover>
    );
  }
}

export default CheckboxMenu;
